/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useRequestMe, useSession } from "@packages/sdk";
import { differenceInCalendarYears } from "date-fns/differenceInCalendarYears";
import { parse } from "date-fns/parse";
import { useForm } from "react-hook-form";

import { spacing } from "../../../../global/emotion";
import { useValidator } from "../../../hooks/useValidator.emotion";
import {
  AuthDOBFormContent,
  dobButtonsStyle,
  dobSchema,
  useConfirmUnderageDialog,
} from "./AuthDOBFormContent.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import type { AuthFlowProps, AuthProps } from "./AuthTypes.emotion";
import { useUnderageErrorDialog } from "./useUnderageErrorDialog";

const headerStyle: Interpolation = {
  height: spacing.xl,
  width: "100%",
};

export const AuthDOBFlow = ({
  hideHeader,
  onAuth,
  className,
  setFlow,
  setState,
}: Omit<AuthFlowProps, "onAuth"> & Pick<AuthProps, "onAuth">) => {
  const { query: meQuery } = useRequestMe();
  const { logout } = useSession();
  const t = useTranslations();
  const analytics = useAnalytics();

  const confirmUnderage = useConfirmUnderageDialog();
  const errorUnderage = useUnderageErrorDialog();

  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } = useValidator<
    ReturnType<typeof dobSchema.parse>
  >(zodResolver(dobSchema));
  const form = useForm({ resolver });

  const { mutationPut } = useRequestMe();
  useViewedScreen({ screen: "birthdate" });

  const onSubmit = async ({ dob }: ReturnType<typeof dobSchema.parse>) => {
    try {
      await mutationPut.mutateAsync({ birthdate: dob });
    } catch (error) {}
    const userEnteredDob = parse(dob, "yyyy-MM-dd", new Date());
    if (Math.abs(differenceInCalendarYears(userEnteredDob, new Date())) < 13) {
      const confirmed = await confirmUnderage.show({ dob: userEnteredDob });
      analytics.track({
        event: HallowAnalyticsEvent.TappedConfirmDOB,
        properties: {
          option: confirmed ? "yes_correct" : "no_cancel",
        },
      });
      if (confirmed) {
        analytics.track({
          event: HallowAnalyticsEvent.ViewedModal,
          properties: {
            modal_name: "under_13_cannot_create_account",
            screen_name: "birthdate",
          },
        });
        await errorUnderage.show();
        logout({
          callback: () => {
            setState({});
            setFlow("main");
          },
        });
      }
      return;
    }
    onAuth({ isNewUser: true });
  };

  return (
    <AuthForm<ReturnType<typeof dobSchema.parse>>
      form={form}
      defaultValues={{
        dob: meQuery.data?.birthdate || "",
      }}
      onSubmit={onSubmit}
      footer={(_, disabled) => (
        <div css={dobButtonsStyle}>
          <AuthSubmitButton disabled={disabled}>
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
      className={className}
    >
      {(form) => (
        <>
          {!hideHeader && <div css={headerStyle} />}
          <AuthDOBFormContent
            form={form}
            errors={errors}
            errorsCSS={errorsCSS}
            fieldsCSS={fieldsCSS}
            inputProps={inputProps}
          />
        </>
      )}
    </AuthForm>
  );
};
